import { Routes, Route, Navigate } from 'react-router-dom';
import { Suspense, lazy } from 'react';
import { MainLayout } from '@/components/layout/MainLayout';
import { ProtectedRoute } from '@/features/auth/components/ProtectedRoute';

// Lazy load pages for code splitting
const DashboardPage = lazy(() =>
  import('@/features/dashboard/components/DashboardPage').then((m) => ({
    default: m.DashboardPage,
  }))
);

const LoginPage = lazy(() =>
  import('@/features/auth/components/LoginPage').then((m) => ({
    default: m.LoginPage,
  }))
);

const CallbackPage = lazy(() =>
  import('@/features/auth/components/CallbackPage').then((m) => ({
    default: m.CallbackPage,
  }))
);

const UsersPage = lazy(() =>
  import('@/features/users/components/UsersPage').then((m) => ({
    default: m.UsersPage,
  }))
);

const UserDetailPage = lazy(() =>
  import('@/features/users/components/UserDetailPage').then((m) => ({
    default: m.UserDetailPage,
  }))
);

const UserProfilePage = lazy(() =>
  import('@/features/users/components/UserProfilePage').then((m) => ({
    default: m.UserProfilePage,
  }))
);

const RolesPage = lazy(() =>
  import('@/features/roles/components/RolesPage').then((m) => ({
    default: m.RolesPage,
  }))
);

const AuditPage = lazy(() =>
  import('@/features/audit/components/AuditPage').then((m) => ({
    default: m.AuditPage,
  }))
);

const ApplicationsPage = lazy(() =>
  import('@/features/applications/components/ApplicationsPage').then((m) => ({
    default: m.ApplicationsPage,
  }))
);

const ScopesPage = lazy(() =>
  import('@/features/scopes/components/ScopesPage').then((m) => ({
    default: m.ScopesPage,
  }))
);

const ScopeDetailPage = lazy(() =>
  import('@/features/scopes/components/ScopeDetailPage').then((m) => ({
    default: m.ScopeDetailPage,
  }))
);

const DeploysPage = lazy(() =>
  import('@/features/deploys/components/DeploysPage').then((m) => ({
    default: m.DeploysPage,
  }))
);

const DeployDetailPage = lazy(() =>
  import('@/features/deploys/components/DeployDetailPage').then((m) => ({
    default: m.DeployDetailPage,
  }))
);

const ServersPage = lazy(() =>
  import('@/features/servers/components/ServersPage').then((m) => ({
    default: m.ServersPage,
  }))
);

const SettingsPage = lazy(() =>
  import('@/features/settings/components/SettingsPage').then((m) => ({
    default: m.SettingsPage,
  }))
);

const RegistriesPage = lazy(() =>
  import('@/features/registries/components/RegistriesPage').then((m) => ({
    default: m.RegistriesPage,
  }))
);

const ImagesPage = lazy(() =>
  import('@/features/images/components/ImagesPage').then((m) => ({
    default: m.ImagesPage,
  }))
);

const PoliciesPage = lazy(() =>
  import('@/features/policies/components/PoliciesPage').then((m) => ({
    default: m.PoliciesPage,
  }))
);

const ApprovalsPage = lazy(() =>
  import('@/features/approvals/components/ApprovalsPage').then((m) => ({
    default: m.ApprovalsPage,
  }))
);

const BackupsPage = lazy(() =>
  import('@/features/backups/components/BackupsPage').then((m) => ({
    default: m.BackupsPage,
  }))
);

const FeatureFlagsPage = lazy(() =>
  import('@/features/featureflags/components/FeatureFlagsPage').then((m) => ({
    default: m.FeatureFlagsPage,
  }))
);

const TeamsPage = lazy(() =>
  import('@/features/teams/components/TeamsPage').then((m) => ({
    default: m.TeamsPage,
  }))
);

const AppConfigPage = lazy(() =>
  import('@/features/config/components/AppConfigPage').then((m) => ({
    default: m.AppConfigPage,
  }))
);

const InfrastructurePage = lazy(() =>
  import('@/features/infrastructure/components/InfrastructurePage').then((m) => ({
    default: m.InfrastructurePage,
  }))
);

// Loading fallback component
function PageLoader() {
  return (
    <div className="flex h-screen items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  );
}

export function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/login/callback" element={<CallbackPage />} />

        {/* Protected routes */}
        <Route
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/profile" element={<UserProfilePage />} />

          {/* Deploy routes */}
          <Route path="/deploys" element={
            <ProtectedRoute requiredPermissions={['deploy:view']}>
              <DeploysPage />
            </ProtectedRoute>
          } />
          <Route path="/deploys/:id" element={
            <ProtectedRoute requiredPermissions={['deploy:view']}>
              <DeployDetailPage />
            </ProtectedRoute>
          } />

          {/* Application routes */}
          <Route path="/apps" element={
            <ProtectedRoute requiredPermissions={['app:view']}>
              <ApplicationsPage />
            </ProtectedRoute>
          } />

          {/* Scope routes */}
          <Route path="/scopes" element={
            <ProtectedRoute requiredPermissions={['scope:view']}>
              <ScopesPage />
            </ProtectedRoute>
          } />
          <Route path="/scopes/:id" element={
            <ProtectedRoute requiredPermissions={['scope:view']}>
              <ScopeDetailPage />
            </ProtectedRoute>
          } />

          {/* Server routes */}
          <Route path="/servers" element={
            <ProtectedRoute requiredPermissions={['server:view']}>
              <ServersPage />
            </ProtectedRoute>
          } />

          {/* Registry & Image routes */}
          <Route path="/registries" element={
            <ProtectedRoute requiredPermissions={['registry:view']}>
              <RegistriesPage />
            </ProtectedRoute>
          } />
          <Route path="/images" element={
            <ProtectedRoute requiredPermissions={['registry:view']}>
              <ImagesPage />
            </ProtectedRoute>
          } />

          {/* Policy routes */}
          <Route path="/policies" element={
            <ProtectedRoute requiredPermissions={['settings:manage']}>
              <PoliciesPage />
            </ProtectedRoute>
          } />

          {/* Approval routes */}
          <Route path="/approvals" element={
            <ProtectedRoute requiredPermissions={['deploy:approve']}>
              <ApprovalsPage />
            </ProtectedRoute>
          } />

          {/* Backup routes */}
          <Route path="/backups" element={
            <ProtectedRoute requiredPermissions={['backup:view']}>
              <BackupsPage />
            </ProtectedRoute>
          } />

          {/* Feature Flag routes */}
          <Route path="/feature-flags" element={
            <ProtectedRoute requiredPermissions={['settings:view']}>
              <FeatureFlagsPage />
            </ProtectedRoute>
          } />

          {/* Teams */}
          <Route path="/teams" element={
            <ProtectedRoute requiredPermissions={['team:view']}>
              <TeamsPage />
            </ProtectedRoute>
          } />

          {/* App Config: Variables & Secrets */}
          <Route path="/config" element={
            <ProtectedRoute requiredPermissions={['scope:view']}>
              <AppConfigPage />
            </ProtectedRoute>
          } />

          {/* Infrastructure */}
          <Route path="/infrastructure" element={
            <ProtectedRoute requiredPermissions={['infrastructure:view']}>
              <InfrastructurePage />
            </ProtectedRoute>
          } />

          {/* Admin routes */}
          <Route path="/users" element={
            <ProtectedRoute requiredPermissions={['user:manage']}>
              <UsersPage />
            </ProtectedRoute>
          } />
          <Route path="/users/:id" element={
            <ProtectedRoute requiredPermissions={['user:manage']}>
              <UserDetailPage />
            </ProtectedRoute>
          } />
          <Route path="/roles" element={
            <ProtectedRoute requiredRoles={['admin-stcaria']}>
              <RolesPage />
            </ProtectedRoute>
          } />
          <Route path="/audit" element={
            <ProtectedRoute requiredPermissions={['audit:view']}>
              <AuditPage />
            </ProtectedRoute>
          } />

          {/* Settings */}
          <Route path="/settings" element={
            <ProtectedRoute requiredPermissions={['settings:view']}>
              <SettingsPage />
            </ProtectedRoute>
          } />
        </Route>

        {/* 404 - Not Found */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Suspense>
  );
}
